import { getMomentTz, MesfModal } from "@dexteel/mesf-core";
import { Button, CircularProgress, Stack, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Moment } from "moment";
import { useEffect } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

const moment = getMomentTz();

type Props = {
	open: boolean;
	maxRev: number;
	isSubmitting: boolean;
	onClose: () => void;
	onSubmit: (payload: {
		rev: number;
		effectiveDate: Date;
		comments: string | null;
	}) => void;
};

type FormValues = {
	rev: number | null;
	effectiveDate: Moment | null;
	comments: string;
};

export const NewVersionModal = ({
	open,
	maxRev,
	isSubmitting,
	onClose,
	onSubmit,
}: Props) => {
	const {
		control,
		handleSubmit,
		reset,
		formState: { isValid },
	} = useForm<FormValues>({
		mode: "onChange",
		defaultValues: { rev: null, effectiveDate: null, comments: "" },
	});

	const submit: SubmitHandler<FormValues> = (data) => {
		onSubmit({
			rev: Number(data.rev),
			effectiveDate: (data.effectiveDate as Moment).toDate(),
			comments: data.comments.trim() ? data.comments.trim() : null,
		});
	};

	useEffect(() => {
		if (open) {
			reset({
				rev: maxRev + 1,
				effectiveDate: moment().add(1, "day"),
				comments: "",
			});
		}
	}, [open, maxRev]);

	return (
		<MesfModal
			open={open}
			handleClose={onClose}
			maxWidth="md"
			title="New Version"
		>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
					handleSubmit(submit)(e);
				}}
			>
				<MesfModal.Content>
					<Stack spacing={2} sx={{ mt: 1 }}>
						<Controller
							name="rev"
							control={control}
							rules={{
								required: "Rev is required",
								validate: (value) =>
									(value != null && Number(value) > maxRev) ||
									`Rev must be greater than the last (${maxRev})`,
							}}
							render={({ field, fieldState }) => (
								<TextField
									label="Rev"
									type="number"
									size="small"
									value={field.value ?? ""}
									onChange={(e) =>
										field.onChange(
											e.target.value === "" ? null : Number(e.target.value),
										)
									}
									error={!!fieldState.error}
									helperText={
										fieldState.error?.message ||
										`Must be greater than ${maxRev}`
									}
								/>
							)}
						/>

						<Controller
							name="effectiveDate"
							control={control}
							rules={{
								validate: {
									required: (value) => !!value || "Effective date is required",
									future: (value) =>
										(value && value.isAfter(moment().endOf("day"))) ||
										"Effective date must be greater than today",
								},
							}}
							render={({ field, fieldState }) => (
								<DatePicker
									label="Effective Date"
									format="MM/DD/YYYY"
									value={field.value}
									minDate={moment().add(1, "day")}
									onChange={(newValue) => field.onChange(newValue)}
									slotProps={{
										textField: {
											size: "small",
											error: !!fieldState.error,
											helperText:
												fieldState.error?.message ||
												"Must be greater than today",
										},
									}}
								/>
							)}
						/>

						<Controller
							name="comments"
							control={control}
							render={({ field }) => (
								<TextField
									label="Comments"
									size="small"
									multiline
									minRows={2}
									value={field.value ?? ""}
									onChange={field.onChange}
								/>
							)}
						/>
					</Stack>
				</MesfModal.Content>
				<MesfModal.Actions>
					<Button onClick={onClose} color="inherit" disabled={isSubmitting}>
						Cancel
					</Button>
					<Button
						type="submit"
						variant="contained"
						disabled={isSubmitting || !isValid}
						startIcon={
							isSubmitting ? (
								<CircularProgress size={16} color="inherit" />
							) : undefined
						}
					>
						Create
					</Button>
				</MesfModal.Actions>
			</form>
		</MesfModal>
	);
};
